import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import api, { listingsAPI, settingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import ListingCard from '@/components/listings/ListingCard';
import FrontpageAnnouncements from '@/components/announcements/FrontpageAnnouncements';
import { Loader2 } from 'lucide-react';
import { useSEO, SEO_KEYWORDS } from '@/hooks/useSEO';

const STORAGE_KEY = 'infinite_scroll_home';
const NAV_FLAG_KEY = 'nav_away_home';
const LOCKED_SCROLL_KEY = 'locked_scroll_home'; // Scroll position locked at navigation time

// Hook to detect if we're on mobile
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    return isMobile;
};

// Read saved state ONCE synchronously - only if user navigated away (not refresh)
const readSavedState = () => {
    try {
        // Check if force fresh was requested (logo click from another page)
        const forceFresh = sessionStorage.getItem('force_fresh_home');
        if (forceFresh) {
            sessionStorage.removeItem('force_fresh_home');
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(NAV_FLAG_KEY);
            sessionStorage.removeItem(LOCKED_SCROLL_KEY);
            return null;
        }
        
        // Check if user navigated away from this page
        const navigatedAway = sessionStorage.getItem(NAV_FLAG_KEY);
        
        // Clear the flag
        sessionStorage.removeItem(NAV_FLAG_KEY);
        
        // Only restore if user navigated away (not on refresh)
        if (!navigatedAway) {
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(LOCKED_SCROLL_KEY);
            return null;
        }
        
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
            const state = JSON.parse(saved);
            if (state && state.listings?.length > 0 && Date.now() - state.timestamp < 10 * 60 * 1000) {
                // Use the locked scroll position if available (more reliable)
                const lockedScroll = sessionStorage.getItem(LOCKED_SCROLL_KEY);
                if (lockedScroll) {
                    state.scrollY = parseInt(lockedScroll, 10);
                    sessionStorage.removeItem(LOCKED_SCROLL_KEY); // Clear after reading
                }
                return state;
            }
        }
    } catch (e) {
        // Ignore
    }
    return null;
};

const HomePage = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    
    // Welcome container visibility state
    const [welcomeHidden, setWelcomeHidden] = useState(() => 
        localStorage.getItem('colectarium_welcome_hidden') === 'true'
    );
    
    // Welcome content from database
    const [welcomeContent, setWelcomeContent] = useState({
        title: 'Bine ai venit pe Colectarium!',
        content: ''
    });
    
    // Load welcome content for non-authenticated users
    useEffect(() => {
        if (!isAuthenticated && !welcomeHidden) {
            api.get('/pages/bine-ai-venit')
                .then(res => {
                    if (res.data) {
                        setWelcomeContent({
                            title: res.data.title || 'Bine ai venit pe Colectarium!',
                            content: res.data.content || ''
                        });
                    }
                })
                .catch(err => console.error('Error loading welcome content:', err));
        }
    }, [isAuthenticated, welcomeHidden]);
    
    // Read saved state ONCE at initialization (synchronous)
    const savedStateRef = useRef(readSavedState());
    const initialState = savedStateRef.current;
    const wasRestored = initialState !== null;
    
    // Initialize state from saved data or defaults
    const [listings, setListings] = useState(initialState?.listings || []);
    const [loading, setLoading] = useState(!wasRestored);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(initialState?.currentPage || 1);
    const [hasMore, setHasMore] = useState(initialState?.hasMore ?? true);
    const [totalItems, setTotalItems] = useState(initialState?.totalItems || 0);
    const [refreshKey, setRefreshKey] = useState(0);
    // Restore grid settings from saved state to prevent layout flash (default to 2 for mobile)
    const [gridSettings, setGridSettings] = useState(initialState?.gridSettings || { desktop: 4, tablet: 3, mobile: 2 });
    const [itemsPerPage] = useState(24);
    
    // Track if we need to fetch (only when NOT restored)
    const shouldFetchRef = useRef(!wasRestored);
    const scrollRestoredRef = useRef(false);
    const reloadTriggeredRef = useRef(false); // Prevents scroll restoration after logo click reload
    const reloadBlockRef = useRef(false); // Block infinite scroll observer during reload
    const loadMoreRef = useRef(null);
    const navigationClickedRef = useRef(false); // Prevents further saves after link click
    
    // Disable browser's automatic scroll restoration - we handle it manually
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        return () => {
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'auto';
            }
        };
    }, []);
    
    // Check for force_fresh_home flag on every mount (handles navigation from other pages)
    useEffect(() => {
        const forceFresh = sessionStorage.getItem('force_fresh_home');
        if (forceFresh) {
            sessionStorage.removeItem('force_fresh_home');
            // Trigger a full reload - but don't set refreshKey since we're already doing a fresh load
            reloadTriggeredRef.current = true;
            scrollRestoredRef.current = true;
            reloadBlockRef.current = true;
            savedStateRef.current = null;
            shouldFetchRef.current = true;
            
            // Clear all saved state
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(NAV_FLAG_KEY);
            sessionStorage.removeItem(LOCKED_SCROLL_KEY);
            
            // Scroll to top
            window.scrollTo({ top: 0, behavior: 'instant' });
            
            // Note: We don't reset listings/currentPage/hasMore here because
            // readSavedState() already returned null and initial state is clean
        }
    }, []); // Run only on mount
    
    // Restore scroll position - wait for content to be ready
    useEffect(() => {
        // Skip restoration if reload was triggered (logo click)
        if (reloadTriggeredRef.current) {
            return;
        }
        
        if (!wasRestored || !initialState?.scrollY || initialState.scrollY <= 0) {
            return;
        }
        
        if (scrollRestoredRef.current) {
            return;
        }
        
        const targetScroll = initialState.scrollY;
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max
        
        const attemptScroll = () => {
            attempts++;
            const docHeight = document.documentElement.scrollHeight;
            const canScroll = docHeight > targetScroll;
            
            if (canScroll || attempts >= maxAttempts) {
                // Perform the scroll
                window.scrollTo({ top: targetScroll, behavior: 'instant' });
                
                // Verify after a short delay
                setTimeout(() => {
                    const actualScroll = window.scrollY;
                    const diff = Math.abs(actualScroll - targetScroll);
                    
                    // If we're not close enough and haven't hit max attempts, retry
                    if (diff > 100 && attempts < maxAttempts) {
                        requestAnimationFrame(attemptScroll);
                    } else {
                        scrollRestoredRef.current = true;
                    }
                }, 100);
            } else {
                // Page not tall enough, wait and retry
                setTimeout(attemptScroll, 100);
            }
        };
        
        // Start restoration after initial render
        const timerId = setTimeout(attemptScroll, 50);
        
        return () => clearTimeout(timerId);
    }, [wasRestored, initialState?.scrollY, listings.length]);
    
    // Save state function (includes gridSettings to prevent layout flash)
    const saveCurrentState = useCallback(() => {
        // Don't save if user clicked a navigation link
        if (navigationClickedRef.current) return;
        
        if (listings.length > 0) {
            const state = {
                listings,
                currentPage,
                hasMore,
                totalItems,
                gridSettings,
                scrollY: window.scrollY,
                timestamp: Date.now()
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
    }, [listings, currentPage, hasMore, totalItems, gridSettings]);
    
    // Clear saved state
    const clearSavedState = useCallback(() => {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(NAV_FLAG_KEY);
        savedStateRef.current = null;
    }, []);

    // SEO
    useSEO({
        title: 'Cumpără și Vinde Piese de Colecție',
        ogTitle: 'Colectarium - Marketplace pentru Colecționari | Numismatică, Faleristică, Efecte militare, Diverse',
        description: 'Colectarium - Marketplace #1 din România pentru colecționari. Cumpără, vinde și participă la licitații pentru piese de numismatică, faleristică și efecte militare.',
        keywords: SEO_KEYWORDS.base,
        url: 'https://colectarium.ro/'
    });

    // ItemList schema for recent listings (helps with rich results)
    const recentListingsSchema = listings.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Cele mai recente piese de colecție pe Colectarium",
        "description": "Piese noi adăugate pe marketplace-ul pentru colecționari",
        "numberOfItems": listings.length,
        "itemListElement": listings.slice(0, 12).map((listing, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "url": `https://colectarium.ro/listing/${listing.slug || listing.listing_id}`,
            "name": listing.title,
            "image": listing.images?.[0] || undefined
        }))
    } : null;

    // Load settings (skip if we already have restored gridSettings)
    useEffect(() => {
        // Skip loading settings if we restored them from sessionStorage
        if (initialState?.gridSettings) return;
        
        const loadSettings = async () => {
            try {
                const settingsRes = await settingsAPI.get();
                if (settingsRes.data) {
                    setGridSettings({
                        desktop: settingsRes.data.grid_columns_desktop || 4,
                        tablet: settingsRes.data.grid_columns_tablet || 3,
                        mobile: settingsRes.data.grid_columns_mobile || 2
                    });
                }
            } catch (error) {
                console.error('Error loading settings:', error);
            }
        };
        loadSettings();
    }, [initialState?.gridSettings]);

    // Save state on scroll (debounced) and before navigation
    useEffect(() => {
        if (listings.length === 0) return;
        
        let scrollTimeout;
        let navigationPending = false; // Flag to stop scroll saves after mousedown on link
        
        const handleScroll = () => {
            // Don't save scroll if user clicked a link (navigation pending)
            if (navigationPending) return;
            
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(saveCurrentState, 200);
        };
        
        // Save immediately on mousedown before any other handler can run
        const handleMouseDown = (e) => {
            const link = e.target.closest('a[href^="/listing/"], a[href^="/category/"], a[href^="/user/"]');
            if (link) {
                // Stop any further scroll/state saves - this is critical!
                navigationClickedRef.current = true;
                navigationPending = true;
                clearTimeout(scrollTimeout);
                
                // Mark that user is navigating away (for back button detection)
                sessionStorage.setItem(NAV_FLAG_KEY, 'true');
                
                // Lock the scroll position in a separate key that won't be overwritten
                const currentScrollY = window.scrollY;
                sessionStorage.setItem(LOCKED_SCROLL_KEY, String(currentScrollY));
                
                // Also save full state
                const state = {
                    listings,
                    currentPage,
                    hasMore,
                    totalItems,
                    gridSettings,
                    scrollY: currentScrollY,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('mousedown', handleMouseDown, { capture: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleMouseDown, { capture: true });
            clearTimeout(scrollTimeout);
        };
    }, [listings, currentPage, hasMore, totalItems, gridSettings, saveCurrentState]);

    // Save state when listings change
    useEffect(() => {
        if (listings.length > 0 && !wasRestored) {
            saveCurrentState();
        }
    }, [listings, currentPage, hasMore, totalItems]);

    // Fetch listings - ONLY when shouldFetchRef is true
    useEffect(() => {
        // Skip if we restored from saved state
        if (!shouldFetchRef.current && currentPage === 1) {
            return;
        }
        
        let isCancelled = false;
        
        const fetchListings = async () => {
            // Capture current page at fetch start to handle concurrent fetches
            const pageToFetch = currentPage;
            
            if (pageToFetch > 1) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            try {
                const skip = (pageToFetch - 1) * itemsPerPage;
                const res = await listingsAPI.getAll({ 
                    limit: itemsPerPage, 
                    skip,
                    sort_by: 'created_at', 
                    sort_order: 'desc' 
                });
                
                // Don't update state if this fetch was cancelled
                if (isCancelled) return;
                
                const data = res.data?.listings || res.data || [];
                const total = res.data?.total || data.length;
                
                // Only append if we're fetching page > 1, otherwise replace
                if (pageToFetch > 1) {
                    setListings(prev => [...prev, ...data]);
                } else {
                    // First page - always replace completely
                    setListings(data);
                }
                
                setTotalItems(total);
                setHasMore(skip + data.length < total);
                
                // Mark that we should fetch on subsequent page changes
                shouldFetchRef.current = true;
            } catch (error) {
                if (!isCancelled) {
                    console.error('Error fetching listings:', error);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                    setLoadingMore(false);
                }
            }
        };
        
        fetchListings();
        
        // Cleanup: cancel this fetch if effect re-runs
        return () => {
            isCancelled = true;
        };
    }, [currentPage, itemsPerPage, refreshKey]);

    // Listen for reload-listings event from Navbar logo click
    useEffect(() => {
        const handleReloadListings = () => {
            // Mark reload as triggered to prevent scroll restoration
            reloadTriggeredRef.current = true;
            scrollRestoredRef.current = true; // Also mark scroll as "done" to prevent any attempts
            reloadBlockRef.current = true; // Block infinite scroll observer
            
            clearSavedState();
            shouldFetchRef.current = true;
            
            // Force reset state synchronously using functional updates
            // This ensures React processes these in order
            setCurrentPage(1);
            setHasMore(true);
            setListings([]); // Clear listings AFTER setting page to 1
            
            // Use a small delay then trigger refresh - this ensures state is updated
            setTimeout(() => {
                setRefreshKey(prev => prev + 1);
            }, 50);
            
            // Ensure scroll to top with multiple attempts
            window.scrollTo({ top: 0, behavior: 'instant' });
            requestAnimationFrame(() => window.scrollTo(0, 0));
            setTimeout(() => window.scrollTo(0, 0), 50);
            setTimeout(() => window.scrollTo(0, 0), 100);
            setTimeout(() => window.scrollTo(0, 0), 200);
            setTimeout(() => window.scrollTo(0, 0), 500);
            setTimeout(() => window.scrollTo(0, 0), 1000);
            setTimeout(() => window.scrollTo(0, 0), 1500);
        };
        
        window.addEventListener('reload-listings', handleReloadListings);
        return () => window.removeEventListener('reload-listings', handleReloadListings);
    }, [clearSavedState]);

    // Force scroll to top after reload completes (when listings change after reload trigger)
    useEffect(() => {
        if (reloadTriggeredRef.current && listings.length > 0) {
            // Listings have loaded after a reload - ensure we're at top
            window.scrollTo({ top: 0, behavior: 'instant' });
            requestAnimationFrame(() => window.scrollTo(0, 0));
            setTimeout(() => {
                window.scrollTo(0, 0);
                // Reset the reload flag after ensuring scroll is at top
                reloadTriggeredRef.current = false;
                // Unblock infinite scroll observer after a delay
                setTimeout(() => {
                    reloadBlockRef.current = false;
                }, 500);
            }, 100);
        }
    }, [listings.length]);

    // Infinite scroll observer - simplified and robust
    const observerRef = useRef(null);
    
    useEffect(() => {
        // Cleanup previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        
        const triggerEl = loadMoreRef.current;
        if (!triggerEl) return;
        if (!hasMore) return;
        if (loading || loadingMore) return;
        
        // Don't create observer during reload (when listings are empty and page is 1)
        if (listings.length === 0 && currentPage === 1) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                // Only trigger if intersecting and not blocked by reload
                if (entry.isIntersecting && !reloadBlockRef.current) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 0.1, rootMargin: '300px' }
        );
        
        observer.observe(triggerEl);
        observerRef.current = observer;
        
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [hasMore, loading, loadingMore, listings.length, currentPage]);

    // Generate grid style based on settings (using CSS variables for dynamic grid)
    const getGridStyle = () => ({
        '--grid-cols-mobile': gridSettings.mobile,
        '--grid-cols-tablet': gridSettings.tablet,
        '--grid-cols-desktop': gridSettings.desktop,
    });

    return (
        <div className="min-h-screen bg-background" data-testid="home-page">
            {/* Additional SEO Structured Data for listings */}
            {recentListingsSchema && (
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify(recentListingsSchema)}
                    </script>
                </Helmet>
            )}
            
            {/* Announcements for logged-in users */}
            {isAuthenticated && <FrontpageAnnouncements />}
            
            {/* Welcome message for non-authenticated users - self-hiding */}
            {!isAuthenticated && !welcomeHidden && (
                <section className="py-6 lg:py-10">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="max-w-2xl mx-auto bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
                            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-5 text-center">
                                {welcomeContent.title}
                            </h1>
                            {/* Render content - split by double newline for paragraphs */}
                            {welcomeContent.content.split('\n\n').map((paragraph, idx) => (
                                <p 
                                    key={idx} 
                                    className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4 text-justify"
                                    dangerouslySetInnerHTML={{
                                        __html: paragraph
                                            .replace(/\*\*(.*?)\*\*/g, '<span class="font-semibold text-foreground">$1</span>')
                                            .replace(/\n/g, '<br />')
                                    }}
                                />
                            ))}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4 mt-6">
                                <Button 
                                    size="lg" 
                                    onClick={() => navigate('/register')}
                                    className="px-8"
                                    data-testid="welcome-register-btn"
                                >
                                    Creează cont gratuit
                                </Button>
                                <Button 
                                    size="lg" 
                                    variant="outline"
                                    onClick={() => navigate('/login')}
                                    className="px-8"
                                    data-testid="welcome-login-btn"
                                >
                                    Am deja cont
                                </Button>
                            </div>
                            <div className="flex justify-center pt-2">
                                <button
                                    onClick={() => {
                                        localStorage.setItem('colectarium_welcome_hidden', 'true');
                                        setWelcomeHidden(true);
                                    }}
                                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
                                    data-testid="welcome-hide-btn"
                                >
                                    <span>Ascunde</span>
                                    <svg 
                                        className="w-4 h-4 transform group-hover:-translate-y-0.5 transition-transform" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            )}
            
            {/* Recent Listings with Infinite Scroll */}
            <section className="py-8 lg:py-12">
                <div className="container mx-auto px-4 lg:px-8">
                    {loading && listings.length === 0 ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : listings.length > 0 ? (
                        <>
                            <div className="grid gap-6 category-grid" style={getGridStyle()}>
                                {listings.map((listing) => (
                                    <ListingCard 
                                        key={listing.listing_id} 
                                        listing={listing} 
                                        singleColumn={isMobile && gridSettings.mobile === 1}
                                    />
                                ))}
                            </div>
                            
                            {/* Infinite scroll loader */}
                            <div 
                                ref={loadMoreRef} 
                                className="flex justify-center py-8"
                                data-testid="infinite-scroll-trigger"
                            >
                                {loadingMore && (
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Se încarcă...</span>
                                    </div>
                                )}
                                {!hasMore && listings.length > 0 && (
                                    <p className="text-muted-foreground text-sm">
                                        Ai văzut toate cele {totalItems} anunțuri
                                    </p>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-16">
                            <h3 className="font-heading text-xl font-bold mb-4">Nicio listare disponibilă</h3>
                            <p className="text-muted-foreground mb-6">
                                Fii primul care adaugă o listare pe platformă!
                            </p>
                            {isAuthenticated ? (
                                <Button 
                                    onClick={() => navigate('/sell')}
                                    className="rounded-full"
                                >
                                    Adaugă prima listare
                                </Button>
                            ) : (
                                <Button 
                                    onClick={() => navigate('/register')}
                                    className="rounded-full"
                                >
                                    Creează cont pentru a lista
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default HomePage;
